import cache from '../../../cache/cache.js';
import RENDERING_DEFAULTS from '../../../constants/rendering.js';
import { ActorRenderMode } from '../../../types/index.js';
import clonePoint3 from '../../../utilities/clonePoint3.js';
import hasOwn from '../../../utilities/hasOwn.js';
import genericViewportDisplaySetMetadataProvider from '../../../utilities/genericViewportDisplaySetMetadataProvider.js';
import { viewportProjection } from '../viewportProjection.js';
import PlanarLegacyCompatibilityController from './PlanarLegacyCompatibilityController.js';
import PlanarViewport from './PlanarViewport.js';
import { cloneDisplayArea } from './planarViewState.js';
import { coreLog } from '../../../utilities/logger.js';
const log = coreLog.getLogger('RenderingEngine', 'PlanarViewportLegacyAdapter');
class PlanarViewportLegacyAdapter extends PlanarViewport {
    constructor() {
        super(...arguments);
        this.legacyCompatibility = new PlanarLegacyCompatibilityController(this.createLegacyCompatibilityHost());
    }
    prefersAcquiredFrames() {
        return false;
    }
    cloneTuple(tuple) {
        if (!tuple) {
            return undefined;
        }
        return Array.from(tuple);
    }
    getCamera() {
        const viewState = this.getViewState();
        const camera = {
            ...viewState,
            ...this.getCameraForEvent(),
        };
        const orientation = viewState.orientation && typeof viewState.orientation === 'object'
            ? {
                ...viewState.orientation,
                viewUp: this.cloneTuple(viewState.orientation.viewUp),
                viewPlaneNormal: this.cloneTuple(viewState.orientation.viewPlaneNormal),
            }
            : viewState.orientation;
        return {
            ...camera,
            focalPoint: this.cloneTuple(camera.focalPoint),
            position: this.cloneTuple(camera.position),
            viewUp: this.cloneTuple(camera.viewUp),
            viewPlaneNormal: this.cloneTuple(camera.viewPlaneNormal),
            orientation,
        };
    }
    setCamera(cameraPatch) {
        const viewStatePatch = {};
        this.applyLegacyPlanarPresentationPatch(viewStatePatch, cameraPatch);
        this.applyLegacyParallelScalePatch(viewStatePatch, cameraPatch);
        this.applyLegacyOrientationPatch(viewStatePatch, cameraPatch);
        this.applyLegacyFocalPointPatch(viewStatePatch, cameraPatch);
        if (cameraPatch.position &&
            !cameraPatch.focalPoint &&
            !Object.keys(viewStatePatch).length) {
            this.warnUnsupportedPositionOnlyCameraPatch();
            return;
        }
        if (!Object.keys(viewStatePatch).length) {
            return;
        }
        this.setViewState(viewStatePatch);
    }
    getViewPresentation(selector) {
        return viewportProjection.getPresentation(this, {
            selector,
        });
    }
    setViewPresentation(viewPres) {
        if (!viewPres) {
            return;
        }
        if (hasOwn(viewPres, 'displayArea')) {
            this.options = {
                ...this.options,
                displayArea: cloneDisplayArea(viewPres.displayArea),
            };
        }
        const nextViewState = viewportProjection.withPresentation(this, viewPres);
        if (nextViewState) {
            this.setViewState(nextViewState);
        }
    }
    resetCamera(options) {
        return this.resetViewState(options);
    }
    resetCameraForResize() {
        return this.resetViewStateForResize();
    }
    get volumeIds() {
        const ids = new Set();
        for (const actorEntry of this.getActors()) {
            const candidates = [actorEntry.referencedId, actorEntry.uid];
            for (const candidate of candidates) {
                if (typeof candidate === 'string' && cache.getVolume(candidate)) {
                    ids.add(candidate);
                }
            }
        }
        return ids;
    }
    get csImage() {
        return this.getCornerstoneImage();
    }
    getCornerstoneImage() {
        const planarData = this.getCurrentBinding()?.data;
        return planarData?.image;
    }
    getAllVolumeIds() {
        return Array.from(this.volumeIds);
    }
    applyLegacyPlanarPresentationPatch(viewStatePatch, cameraPatch) {
        if (hasOwn(cameraPatch, 'orientation')) {
            viewStatePatch.orientation = cameraPatch.orientation;
        }
        if (hasOwn(cameraPatch, 'anchorWorld')) {
            viewStatePatch.anchorWorld = clonePoint3(cameraPatch.anchorWorld);
        }
        if (hasOwn(cameraPatch, 'anchorCanvas')) {
            viewStatePatch.anchorCanvas = cameraPatch.anchorCanvas
                ? [...cameraPatch.anchorCanvas]
                : undefined;
        }
        if (hasOwn(cameraPatch, 'scale')) {
            viewStatePatch.scale = cameraPatch.scale;
        }
        if (hasOwn(cameraPatch, 'scaleMode')) {
            viewStatePatch.scaleMode = cameraPatch.scaleMode;
        }
        if (hasOwn(cameraPatch, 'rotation')) {
            viewStatePatch.rotation = cameraPatch.rotation;
        }
        if (hasOwn(cameraPatch, 'flipHorizontal')) {
            viewStatePatch.flipHorizontal = cameraPatch.flipHorizontal;
        }
        if (hasOwn(cameraPatch, 'flipVertical')) {
            viewStatePatch.flipVertical = cameraPatch.flipVertical;
        }
        if (hasOwn(cameraPatch, 'displayArea')) {
            viewStatePatch.displayArea = cameraPatch.displayArea;
        }
        if (hasOwn(cameraPatch, 'slice')) {
            viewStatePatch.slice = cameraPatch.slice;
        }
    }
    applyLegacyParallelScalePatch(viewStatePatch, cameraPatch) {
        if (typeof cameraPatch.parallelScale !== 'number') {
            return;
        }
        const resolvedICamera = this.getResolvedView()?.toICamera();
        if (!resolvedICamera || typeof resolvedICamera.parallelScale !== 'number') {
            return;
        }
        const currentScale = this.getScale();
        const currentScaleY = Math.max(resolvedICamera.presentationScale?.[1] ?? currentScale[1] ?? 1, 1e-6);
        const currentScaleX = Math.max(resolvedICamera.presentationScale?.[0] ??
            currentScale[0] ??
            currentScaleY, 1e-6);
        const fitParallelScale = resolvedICamera.parallelScale * currentScaleY;
        const nextScaleY = fitParallelScale / Math.max(cameraPatch.parallelScale, 1e-6);
        const aspectScale = currentScaleX / currentScaleY;
        viewStatePatch.displayArea = undefined;
        viewStatePatch.scale = [nextScaleY * aspectScale, nextScaleY];
        viewStatePatch.scaleMode = 'fit';
    }
    applyLegacyOrientationPatch(viewStatePatch, cameraPatch) {
        if (!cameraPatch.viewPlaneNormal) {
            return;
        }
        const currentICamera = this.getResolvedView()?.toICamera();
        viewStatePatch.orientation = {
            viewPlaneNormal: [...cameraPatch.viewPlaneNormal],
            viewUp: cameraPatch.viewUp
                ? [...cameraPatch.viewUp]
                : currentICamera?.viewUp
                    ? [...currentICamera.viewUp]
                    : undefined,
        };
    }
    applyLegacyFocalPointPatch(viewStatePatch, cameraPatch) {
        if (!cameraPatch.focalPoint) {
            return;
        }
        const focalPoint = [...cameraPatch.focalPoint];
        viewStatePatch.anchorCanvas = [0.5, 0.5];
        viewStatePatch.anchorWorld = focalPoint;
        viewStatePatch.displayArea = undefined;
        if (this.isVolumeSliceRenderingActive()) {
            viewStatePatch.slice = {
                kind: 'volumePoint',
                sliceWorldPoint: focalPoint,
            };
        }
    }
    isVolumeSliceRenderingActive() {
        const rendering = this.getCurrentPlanarRendering();
        return (rendering?.renderMode === ActorRenderMode.CPU_VOLUME ||
            rendering?.renderMode === ActorRenderMode.VTK_VOLUME_SLICE);
    }
    warnUnsupportedPositionOnlyCameraPatch() {
        if (typeof process !== 'undefined' &&
            process.env?.NODE_ENV === 'production') {
            return;
        }
        log.warn('[PlanarViewportLegacyAdapter] Position-only planar camera patches are unsupported. Use focalPoint, parallelScale, or view state APIs.');
    }
    removeData(dataId) {
        super.removeData(dataId);
        this.legacyCompatibility.removeData(dataId);
    }
    getActor(actorEntryUID) {
        return this.getActors().find((actorEntry) => actorEntry.uid === actorEntryUID);
    }
    removeActors(actorEntryUIDs) {
        let didRemoveActor = false;
        actorEntryUIDs
            .filter((actorEntryUID) => typeof actorEntryUID === 'string')
            .forEach((actorEntryUID) => {
            const bindingDataId = this.findBindingDataIdByActorEntryUID(actorEntryUID);
            if (bindingDataId) {
                if (this.getDisplaySetRole(bindingDataId) === 'overlay') {
                    genericViewportDisplaySetMetadataProvider.remove(bindingDataId);
                }
                this.removeData(bindingDataId);
                didRemoveActor = true;
            }
        });
        if (didRemoveActor) {
            this.render();
        }
    }
    async setStack(imageIds, currentImageIdIndex = 0) {
        return this.legacyCompatibility.setStack(imageIds, currentImageIdIndex);
    }
    async setVolumes(volumeInputArray, immediate = false, suppressEvents = false) {
        return this.legacyCompatibility.setVolumes(volumeInputArray, immediate, suppressEvents);
    }
    async addVolumes(volumeInputArray, immediate = false, suppressEvents = false) {
        return this.legacyCompatibility.addVolumes(volumeInputArray, immediate, suppressEvents);
    }
    setProperties(properties = {}, volumeIdOrSuppressEvents, suppressEvents = false) {
        this.legacyCompatibility.setProperties(properties, volumeIdOrSuppressEvents, suppressEvents);
    }
    getProperties(volumeId) {
        return this.legacyCompatibility.getProperties(volumeId);
    }
    resetProperties(volumeId) {
        this.legacyCompatibility.resetProperties(volumeId);
    }
    setDefaultProperties(properties = {}, imageId) {
        this.legacyCompatibility.setDefaultProperties(properties, imageId);
    }
    clearDefaultProperties(imageId) {
        this.legacyCompatibility.clearDefaultProperties(imageId);
    }
    resetToDefaultProperties() {
        this.legacyCompatibility.resetToDefaultProperties();
    }
    getBlendMode(filterActorUIDs) {
        return this.legacyCompatibility.getBlendMode(filterActorUIDs);
    }
    setBlendMode(blendMode, filterActorUIDs, immediate = false) {
        this.legacyCompatibility.setBlendMode(blendMode, filterActorUIDs, immediate);
    }
    getSlabThickness() {
        let slabThickness = RENDERING_DEFAULTS.MINIMUM_SLAB_THICKNESS;
        for (const actor of this.getActors()) {
            if (actor.slabThickness > slabThickness) {
                slabThickness = actor.slabThickness;
            }
        }
        return slabThickness;
    }
    getNumberOfSlices() {
        return this.legacyCompatibility.getNumberOfSlices();
    }
    onDestroy() {
        this.legacyCompatibility.destroy();
        super.onDestroy();
    }
}
export default PlanarViewportLegacyAdapter;
